/**
 * Carousel configuration constants for different carousel types
 * Used by useVirtualizedCarousel hook for reusable carousel logic
 */

export interface CarouselConfig {
    SLIDE_ROW_GAP: number;
    SLIDE_COLUMN_GAP: number;
    SLIDE_ITEM_WIDTH: number;
    SLIDE_ITEM_HEIGHT: {
        NORMAL: number;
        VERTICAL_HALF_SCREEN: number;
    };
    NAVIGATION_BUTTON_OFFSET: number;
    CONTAINER_PADDING: number;
    ITEMS_PER_ROW: {
        HORIZONTAL: number;
        VERTICAL: number;
    };
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
        VERTICAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
    };
    VIRTUAL_CACHE_SIZE: number;
    PRELOAD_SLIDES: number;
}

// Shop carousel configuration
export const SHOP_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_COLUMN_GAP: 24,
    SLIDE_ITEM_WIDTH: 424,
    SLIDE_ITEM_HEIGHT: {
        NORMAL: 148,
        VERTICAL_HALF_SCREEN: 114,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: 3,
        VERTICAL: 2,
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 2,
        },
        VERTICAL: {
            FULL_SCREEN: 6,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// FnB carousel configuration
export const FNB_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_COLUMN_GAP: 24,
    SLIDE_ITEM_WIDTH: 424,
    SLIDE_ITEM_HEIGHT: {
        NORMAL: 148,
        VERTICAL_HALF_SCREEN: 114,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: 3,
        VERTICAL: 2,
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 2,
        },
        VERTICAL: {
            FULL_SCREEN: 6,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Facility carousel configuration
export const FACILITY_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_COLUMN_GAP: 16,
    SLIDE_ITEM_WIDTH: 200,
    SLIDE_ITEM_HEIGHT: {
        NORMAL: 214,
        VERTICAL_HALF_SCREEN: 124,
    },
    NAVIGATION_BUTTON_OFFSET: 100,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: 5,
        VERTICAL: 3,
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Default carousel configuration - can be used as base for new carousels
export const DEFAULT_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_COLUMN_GAP: 20,
    SLIDE_ITEM_WIDTH: 300,
    SLIDE_ITEM_HEIGHT: {
        NORMAL: 180,
        VERTICAL_HALF_SCREEN: 140,
    },
    NAVIGATION_BUTTON_OFFSET: 80,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: 4,
        VERTICAL: 3,
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 25,
    PRELOAD_SLIDES: 2,
} as const;

// Carousel type enum for easy identification
export enum CarouselType {
    SHOP = 'shop',
    FNB = 'fnb',
    FACILITY = 'facility',
    DEFAULT = 'default',
}

// Helper function to get carousel config by type
export const getCarouselConfig = (type: CarouselType): CarouselConfig => {
    switch (type) {
        case CarouselType.SHOP:
            return SHOP_CAROUSEL_CONFIG;
        case CarouselType.FNB:
            return FNB_CAROUSEL_CONFIG;
        case CarouselType.FACILITY:
            return FACILITY_CAROUSEL_CONFIG;
        case CarouselType.DEFAULT:
        default:
            return DEFAULT_CAROUSEL_CONFIG;
    }
};
